import dayjs from "dayjs";
import { flowRight } from "lodash-es";

import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

const datesFormat = {
	/**
	 * Date (in local format)
	 */
	LOCALE: "L",
	/**
	 * Day of week, month name, day of month, year, time (Thursday, September 4 1986 8:30 PM)
	 */
	LOCALE_FULL: "LLLL",
	/**
	 * Month name, day of month, year, time (September 4 1986 8:30 PM)
	 */
	LOCALE_FULL_2: "LLL",
	/**
	 * Month name, day of month, year (September 4 1986)
	 */
	LOCALE_FULL_3: "LL",
	/**
	 * Week day (Sunday Monday ... Friday Saturday)
	 */
	WEEK_DAY: "dddd",
	/**
	 * Month name, year (August 2022)
	 */
	MONTH_YEAR: "MMMM YYYY",
	/**
	 * Locale date (08/16/2018)
	 */
	DATE: "L",
	/**
	 * Locale date, hour (08/29/2022 10:05 AM)
	 */
	DATE_HOUR: "L LT",
	/**
	 * ISO_8601 (2022-08-29)
	 */
	ISO_8601: "YYYY-MM-DD",
	/**
	 * Locale hour (10:05 AM)
	 */
	HOUR: "LT",
	/**
	 * 24 hour (22:05)
	 */
	HOUR_24: "HH:mm",
	/**
	 * 24 hour (22:05:00)
	 */
	HOUR_SECONDS: "HH:mm:ss",
} as const;

export type DateFormat = keyof typeof datesFormat;

type SingularTime = "year" | "month" | "week" | "day" | "hour" | "minute" | "second";
type PluralTime = `${SingularTime}s`;

type TimeDuration = SingularTime | PluralTime;

export interface CustomFormat {
	custom: string;
}

function formatDate<T = DateFormat>(
	date: Date,
	format?: T extends DateFormat ? T : CustomFormat,
): string {
	if (typeof format !== "object") {
		const defaultFormat = format ?? "LOCALE";
		return dayjs(date).format(datesFormat[defaultFormat as DateFormat]);
	}
	return dayjs(date).format(format.custom);
}

// Use it only for compose datetime, otherwise use datetime object

function add(amount: number, duration: TimeDuration) {
	return (date: Date) => dayjs(date).add(amount, duration).toDate();
}

function subtract(amount: number, duration: TimeDuration) {
	return (date: Date) => dayjs(date).subtract(amount, duration).toDate();
}

function startOf(duration: TimeDuration) {
	return (date: Date) => dayjs(date).startOf(duration).toDate();
}

function endOf(duration: TimeDuration) {
	return (date: Date) => dayjs(date).endOf(duration).toDate();
}

function isAfter(comparisonDate: Date) {
	return (date: Date) => dayjs(date).isAfter(comparisonDate);
}

function isBefore(comparisonDate: Date) {
	return (date: Date) => dayjs(date).isBefore(comparisonDate);
}

function isBetween(since: Date, until: Date) {
	return (date: Date) => dayjs(date).isBetween(since, until);
}

function isSame(comparisonDate: Date) {
	return (date: Date) => dayjs(date).isSame(comparisonDate);
}

function fromNow(withoutSuffix?: boolean) {
	return (date: Date) => dayjs(date).fromNow(withoutSuffix);
}

function diff(comparisonDate: Date, duration: TimeDuration) {
	return (date: Date) => dayjs(date).diff(comparisonDate, duration);
}

const datetime = (date = dayjs().toDate()) => ({
	now() {
		return date;
	},
	isToday() {
		return dayjs(date).isToday();
	},
	isYesterday() {
		return dayjs(date).isYesterday();
	},
	add(amount: number, duration: TimeDuration) {
		return add(amount, duration)(date);
	},
	subtract(amount: number, duration: TimeDuration) {
		return subtract(amount, duration)(date);
	},
	startOf(duration: TimeDuration) {
		return startOf(duration)(date);
	},
	endOf(duration: TimeDuration) {
		return endOf(duration)(date);
	},
	isAfter(comparisonDate: Date) {
		return isAfter(comparisonDate)(date);
	},
	isBefore(comparisonDate: Date) {
		return isBefore(comparisonDate)(date);
	},
	isBetween(since: Date, until: Date) {
		return isBetween(since, until)(date);
	},
	isSame(comparisonDate: Date) {
		return isSame(comparisonDate)(date);
	},
	fromNow(withoutSuffix?: true) {
		return fromNow(withoutSuffix)(date);
	},
	diff(comparisonDate: Date, duration: TimeDuration) {
		return diff(comparisonDate, duration)(date);
	},
	compose(...args: ((date: Date) => Date)[]): Date {
		return flowRight(...args)(date);
	},
});

const getNextBusinessDay = (date: Date): Date => {
	const currentDate = dayjs(date);

	if (![0, 6].includes(currentDate.day())) {
		return date;
	}

	return getNextBusinessDay(add(1, "day")(date));
};

/**
 * Convert dayjs to local date without timezone offset
 * This function creates a Date object that when serialized to JSON,
 * maintains the local date/time without UTC conversion
 */
const toLocalDate = (dayjsDate: dayjs.Dayjs, isEndOfDay = false): Date => {
	const year = dayjsDate.year();
	const month = dayjsDate.month();
	const day = dayjsDate.date();

	const END_OF_DAY_HOUR = 23;
	const END_OF_DAY_MINUTE = 59;
	const END_OF_DAY_SECOND = 59;
	const END_OF_DAY_MILLISECOND = 999;

	let date: Date;
	if (isEndOfDay) {
		date = new Date(
			year,
			month,
			day,
			END_OF_DAY_HOUR,
			END_OF_DAY_MINUTE,
			END_OF_DAY_SECOND,
			END_OF_DAY_MILLISECOND,
		);
	} else {
		date = new Date(year, month, day, 0, 0, 0, 0);
	}

	// Override toISOString to return local time without UTC conversion
	date.toISOString = function () {
		const TWO_DIGITS = 2;
		const THREE_DIGITS = 3;
		const pad = (num: number) => num.toString().padStart(TWO_DIGITS, "0");
		const padMs = (num: number) => num.toString().padStart(THREE_DIGITS, "0");

		const year = this.getFullYear();
		const month = pad(this.getMonth() + 1);
		const day = pad(this.getDate());
		const hours = pad(this.getHours());
		const minutes = pad(this.getMinutes());
		const seconds = pad(this.getSeconds());
		const milliseconds = padMs(this.getMilliseconds());

		return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
	};

	// Override toJSON to use our custom toISOString
	date.toJSON = function () {
		return this.toISOString();
	};

	return date;
};

/**
 * Format date with Brasília timezone (GMT-3)
 * @param date - Date to format
 * @param format - Optional format string (defaults to DD/MM/YYYY HH:mm)
 */
const formatDateBrasilia = (date: Date, format: string = "DD/MM/YYYY HH:mm"): string => {
	return dayjs(date).tz("America/Sao_Paulo").format(format);
};

export {
	datetime,
	subtract,
	startOf,
	endOf,
	add,
	getNextBusinessDay,
	formatDate,
	formatDateBrasilia,
	toLocalDate,
};
